import type { Expense } from '@coreledger/shared-types';
import { api, API_BASE } from './client';
import type { PaginationMeta } from './invoices';

export interface Paginated<T> { data: T[]; meta: PaginationMeta }

export interface ExpenseListFilter {
  category?: string;
  vendor?: string;
  mode?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

const tok = () => sessionStorage.getItem('cl_token') ?? '';

export const expensesApi = {
  // Filters are applied server-side (ExpensesController/ExpensesService
  // already supported category/dateFrom/dateTo/search — vendor/mode are new
  // here) rather than the old client-side narrowing of just the current
  // page's 50 rows, which silently missed matches on any other page.
  list: (page = 1, limit = 50, filter: ExpenseListFilter = {}) => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filter.category) params.set('category', filter.category);
    if (filter.vendor) params.set('vendor', filter.vendor);
    if (filter.mode) params.set('mode', filter.mode);
    if (filter.search) params.set('search', filter.search);
    if (filter.dateFrom) params.set('dateFrom', filter.dateFrom);
    if (filter.dateTo) params.set('dateTo', filter.dateTo);
    return api.get<Paginated<Expense>>(`/expenses?${params.toString()}`);
  },
  get: (id: string) => api.get<Expense>(`/expenses/${id}`),
  create: (data: Partial<Expense>) => api.post<Expense>('/expenses', data),
  update: (id: string, data: Partial<Expense>) => api.patch<Expense>(`/expenses/${id}`, data),
  delete: (id: string) => api.delete<void>(`/expenses/${id}`),

  uploadReceipt: async (id: string, file: File): Promise<Expense> => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_BASE}/expenses/${id}/receipt`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${tok()}` },
      body: fd,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({})) as { message?: string };
      throw new Error(body.message ?? `Upload failed (${res.status})`);
    }
    return res.json() as Promise<Expense>;
  },

  receiptDownloadUrl: (id: string) => `${API_BASE}/expenses/${id}/receipt`,
};
